import React, { useMemo } from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import {
  COMPANY_OBJECTIVES_TAB_OVERVIEW,
  COMPANY_OBJECTIVES_TAB_PARTICIPATION,
  COMPANY_OBJECTIVES_TAB_STRATEGIC,
} from '../../consts/consts';
import type {
  Features,
  Me,
  OrganizationSettings,
  ReduxState,
  Tab,
} from '../../types';
import { OBJECTIVE_VIEW_TYPES } from '../../utils/models/Objective';
import Page from '../Layout/Pages/Page';
import PersonalObjectives from '../Person/PersonalObjectives';
import ObjectivesOverviewPage from './ObjectivesOverviewPage';
import ObjectivesParticipationPage from './ObjectivesParticipationPage';
import { useStrategicViewAccess } from './ObjectiveTypeOperations';

interface Props {
  features: Features;
  settings: OrganizationSettings;
  me: Me;
}

const insertIf = (condition: boolean, ...elements: Tab[]) =>
  condition ? elements : [];

const ObjectivesHubPage: React.FC<Props> = ({ features, settings, me }) => {
  const { formatMessage }: IntlShape = useIntl();

  const hasStrategicViewAccess = useStrategicViewAccess();
  const hasStrategicTab = !!me && hasStrategicViewAccess;

  const tabs: Tab[] = useMemo(
    () => [
      ...insertIf(hasStrategicTab, {
        ...COMPANY_OBJECTIVES_TAB_STRATEGIC(formatMessage),
        content: (
          <PersonalObjectives
            person={me}
            viewType={OBJECTIVE_VIEW_TYPES.STRATEGIC}
            className="mb-n4 col-12"
          />
        ),
      }),
      ...insertIf(features?.objectives?.rollup_enabled ?? true, {
        ...COMPANY_OBJECTIVES_TAB_OVERVIEW(formatMessage, {
          isDefault: !hasStrategicTab,
        }),
        content: <ObjectivesOverviewPage />,
      }),
      ...insertIf(
        !settings?.objectives_restrict_visibility_to_match_editability,
        {
          ...COMPANY_OBJECTIVES_TAB_PARTICIPATION(formatMessage),
          content: <ObjectivesParticipationPage />,
        }
      ),
    ],
    [
      formatMessage,
      features?.objectives?.rollup_enabled,
      hasStrategicTab,
      settings?.objectives_restrict_visibility_to_match_editability,
      me,
    ]
  );

  return (
    <Page
      title={formatMessage({
        id: 'app.views.objectives.objectives_hub_page.title.objectives',
        defaultMessage: 'Objectives',
      })}
      tabs={tabs}
    />
  );
};

const mapStateToProps = (state: ReduxState) => {
  const { authUser, features, me, settings } = state;
  return {
    features,
    authUser,
    me,
    settings,
  };
};

const connector = connect(mapStateToProps);

export default connector(React.memo(ObjectivesHubPage));
